import type { IFarm } from 'interfaces/IFarm';
import type { IInput } from 'interfaces/IInput';
import { ZoneLinkType } from 'interfaces/IInput';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { convertToHectares } from 'utils/unitConversion';
import type {
  AnalysisColumnKey,
  AnalysisGroupBy,
  AnalysisPriceScenario,
  AnalysisProductionScenario,
  SummaryEntityCategory,
} from '../../../../redux/slices/InsightsSlice';
import { computeGrossMargin } from './computeGrossMargin';
import {
  COST_CATEGORY_ORDER,
  STEP_TYPE_TO_COST_CATEGORY,
} from 'pages/FarmsPage/Workspace/Insights/costCategories';

/** Cost categories shown as columns, in display order — the shared set from costCategories.ts. */
export const ANALYSIS_COST_CATEGORIES = COST_CATEGORY_ORDER;

/** Every optional Analysis grid column, in display order — drives both the column picker and the copy-to-clipboard export. Paddock is excluded (always shown). */
export const ANALYSIS_COLUMN_ORDER: AnalysisColumnKey[] = [
  'farmName',
  'areaHa',
  'crop',
  'variety',
  'startDate',
  'price',
  'yield',
  'production',
  'income',
  ...ANALYSIS_COST_CATEGORIES,
  'totalCosts',
  'grossMargin',
  'costPercentIncome',
];

/** Step type → cost category — the shared mapping from costCategories.ts (Seed dress counts as Seed). */
export const STEP_TYPE_TO_SUMMARY_ENTITY = STEP_TYPE_TO_COST_CATEGORY;

const PRICE_SCENARIO_INDEX: Record<AnalysisPriceScenario, 0 | 1 | 2> = {
  pessimistic: 0,
  expected: 1,
  optimistic: 2,
};

const PRODUCTION_SCENARIO_INDEX: Record<AnalysisProductionScenario, 0 | 1 | 2> = {
  low: 0,
  expected: 1,
  potential: 2,
};

export interface AnalysisRow {
  inputId: string;
  /** Null when the paddock can no longer be found (e.g. deleted). */
  paddockId: string | null;
  paddockName: string;
  farmName: string;
  areaHa: number;
  crop: string;
  variety: string;
  /** ISO date string, or null when the plan has no start date set. */
  startDate: string | null;
  price: number;
  yield: number;
  yieldUnit: string;
  production: number;
  incomeTotal: number;
  incomePerHa: number;
  costsTotal: Partial<Record<SummaryEntityCategory, number>>;
  costsPerHa: Partial<Record<SummaryEntityCategory, number>>;
  totalCostsTotal: number;
  totalCostsPerHa: number;
  grossMarginTotal: number;
  grossMarginPerHa: number;
  /** Total costs ÷ income. Null when there's no income to divide by. */
  costPercentIncome: number | null;
}

/**
 * Resolve paddock/farm names for an input by joining its zone links against
 * live farm/paddock data (rather than the input's own possibly-stale
 * `snapLinkName`/`zone.zoneName` snapshot). Falls back to the snapshot when
 * the paddock or farm can no longer be found (e.g. deleted).
 */
export const resolveInputLocation = (
  input: IInput,
  farms: IFarm[]
): { paddockId: string | null; paddockName: string; farmName: string } => {
  const links = input.zone?.links ?? [];
  const paddockLink = links.find((l) => l.linkType === ZoneLinkType.Paddock);
  const farmLink = links.find((l) => l.linkType === ZoneLinkType.Farm);

  let farm: IFarm | undefined;
  let paddock: IFarm['paddocks'][number] | undefined;

  if (paddockLink) {
    for (const candidate of farms) {
      const match = candidate.paddocks.find(
        (p) => p.paddockId === paddockLink.linkId
      );
      if (match) {
        farm = candidate;
        paddock = match;
        break;
      }
    }
  }
  if (!farm && farmLink) {
    farm = farms.find((f) => f.id === farmLink.linkId);
  }

  return {
    paddockId: paddock?.paddockId ?? null,
    paddockName:
      paddock?.paddockName ??
      paddockLink?.snapLinkName ??
      input.zone?.zoneName ??
      input.docName ??
      'Unnamed paddock',
    farmName: farm?.farmName ?? farmLink?.snapLinkName ?? '',
  };
};

/**
 * Resolve a paddock's area directly from its zone — a fixed property of the
 * input document, unaffected by which task category (Plan/Actual) is
 * selected. Deliberately NOT `computeGrossMargin`'s `refArea`, which is an
 * average of whichever tasks/steps match the selected category and can
 * therefore shift between Plan and Actual if their steps recorded different
 * areas (e.g. actual coverage differing from the plan).
 */
const resolveInputAreaHa = (input: IInput, gUnits: GUnit[]): number => {
  const zoneArea = input.zone?.zoneArea ?? 0;
  if (zoneArea <= 0) return 0;
  return convertToHectares(zoneArea, input.zone?.areaUnit ?? null, gUnits) ?? zoneArea;
};

/**
 * Compute one Analysis row per paddock plan document, at the selected
 * Price/Production scenario cell. Reuses `computeGrossMargin`'s per-input
 * math (revenue/GM 3×3, cost breakdown) rather than re-deriving it — this
 * function only selects a scenario cell and reshapes the cost breakdown
 * into Summary-aligned category columns.
 */
export const computeAnalysis = (
  inputs: IInput[],
  farms: IFarm[],
  category: InputTaskCategory,
  priceScenario: AnalysisPriceScenario,
  productionScenario: AnalysisProductionScenario,
  gUnits: GUnit[]
): AnalysisRow[] => {
  const categories = new Set([category]);
  const pi = PRICE_SCENARIO_INDEX[priceScenario];
  const yi = PRODUCTION_SCENARIO_INDEX[productionScenario];

  const rows: AnalysisRow[] = [];

  for (const input of inputs) {
    const gm = computeGrossMargin(input, categories, gUnits);
    // A paddock with no tasks recorded for the selected category (e.g. no
    // Actuals logged yet) still gets a row, with 0s for every derived
    // figure, rather than disappearing from the grid — its identifying
    // columns (paddock/farm/crop/variety/area) are unaffected either way.

    const { paddockId, paddockName, farmName } = resolveInputLocation(
      input,
      farms
    );
    const areaHa = resolveInputAreaHa(input, gUnits);
    const price = gm.refPrices[pi];
    const yieldVal = gm.refYields[yi];
    const incomeTotal = gm.revenue[yi][pi];
    const grossMarginTotal = gm.gm[yi][pi];
    const totalCostsTotal = Math.abs(gm.tvcTotal);

    const costsTotal: Partial<Record<SummaryEntityCategory, number>> = {};
    const costsPerHa: Partial<Record<SummaryEntityCategory, number>> = {};
    for (const line of gm.costBreakdown) {
      const cat = STEP_TYPE_TO_SUMMARY_ENTITY[line.stepType];
      if (!cat) continue;
      costsTotal[cat] = (costsTotal[cat] ?? 0) + line.totalCost;
      costsPerHa[cat] = (costsPerHa[cat] ?? 0) + line.costPerHa;
    }

    rows.push({
      inputId: input.id,
      paddockId,
      paddockName,
      farmName,
      areaHa,
      crop: input.crop.cropName,
      variety: input.crop.variety?.varietyName ?? '',
      startDate: input.startDate,
      price,
      yield: yieldVal,
      // computeGrossMargin defaults yieldUnit to 't' rather than a blank
      // sentinel — with no matching tasks that default never got
      // overwritten by a real production step, so it's a placeholder, not
      // an actual recorded unit. Blank it here rather than show a unit
      // that was never really measured.
      yieldUnit: gm.hasData ? gm.yieldUnit : '',
      production: yieldVal * areaHa,
      incomeTotal,
      incomePerHa: areaHa > 0 ? incomeTotal / areaHa : 0,
      costsTotal,
      costsPerHa,
      totalCostsTotal,
      totalCostsPerHa: areaHa > 0 ? totalCostsTotal / areaHa : 0,
      grossMarginTotal,
      grossMarginPerHa: areaHa > 0 ? grossMarginTotal / areaHa : 0,
      costPercentIncome: incomeTotal > 0 ? totalCostsTotal / incomeTotal : null,
    });
  }

  return rows.sort((a, b) => {
    if (a.farmName !== b.farmName) {
      return a.farmName.localeCompare(b.farmName, undefined, {
        sensitivity: 'base',
      });
    }
    return a.paddockName.localeCompare(b.paddockName, undefined, {
      sensitivity: 'base',
    });
  });
};

/**
 * The group a row belongs to under the given grouping mode — shared by
 * AnalysisGrid (rendering group sections) and AnalysisView (deriving the set
 * of group keys for the Collapse/Expand-all control) so both always agree on
 * what a "group" is.
 */
export const resolveGroupKey = (
  row: AnalysisRow,
  groupBy: AnalysisGroupBy
): string => {
  if (groupBy === 'farm') return row.farmName || 'Unassigned Farm';
  return row.variety
    ? `${row.crop} — ${row.variety}`
    : row.crop || 'Unspecified Crop';
};

/**
 * Distinct yield units across the given rows, compared case/whitespace
 * insensitively ("t" vs "T" vs " t " count as one) so a labelling
 * inconsistency doesn't register as a real unit difference. Original
 * (first-seen) casing is preserved for display. Shared by AnalysisGrid
 * (header/footer consolidation) and AnalysisView (copy-to-clipboard), so
 * both agree on whether the paddocks in view share one unit.
 */
export const resolveDistinctYieldUnits = (rows: AnalysisRow[]): string[] => {
  const seen = new Map<string, string>();
  for (const r of rows) {
    const key = r.yieldUnit.trim().toLowerCase();
    if (key && !seen.has(key)) seen.set(key, r.yieldUnit);
  }
  return Array.from(seen.values());
};
